import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Check, HeartHandshake, ShieldCheck, Users, Upload, Loader2 } from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { supabase } from "@/integrations/supabase/client";
import { createResumeUpload, submitJobApplication } from "@/lib/careers.functions";
import heroImg from "@/assets/assistido/assistido-2.jpg.asset.json";

const TITLE = "Trabalhe conosco | Nook Gym";
const DESCRIPTION =
  "Faça parte do time Nook. Envie seu currículo para o banco de talentos das unidades Recreio, Barra da Tijuca e Américas.";

export const Route = createFileRoute("/trabalhe-conosco")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://nookgym.com.br/trabalhe-conosco" },
      { name: "robots", content: "index, follow" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://nookgym.com.br/trabalhe-conosco" }],
  }),
  component: Page,
});

const AREAS = [
  "Professor(a) de Educação Física",
  "Estágio em Educação Física",
  "Recepção e atendimento",
  "Comercial",
  "Administrativo",
  "Limpeza e conservação",
  "Marketing",
  "Outra área",
];

const UNIT_OPTIONS = [
  "Recreio dos Bandeirantes",
  "Barra da Tijuca",
  "Qualquer localidade",
];

const AVAILABILITY = ["Manhã", "Tarde", "Noite", "Horário flexível"];

const PROFILE = [
  {
    icon: HeartHandshake,
    title: "Presença e cuidado",
    text: "Estar junto do aluno, com atenção\nreal do início ao fim da sessão.",
  },
  {
    icon: ShieldCheck,
    title: "Comprometimento e organização",
    text: "Pontualidade, responsabilidade e\ncuidado com cada detalhe da operação.",
  },
  {
    icon: Users,
    title: "Espírito de equipe",
    text: "Trabalho conjunto, comunicação\nclara e apoio entre o time todo.",
  },
];

const MAX_SIZE = 5 * 1024 * 1024;

const inputClass =
  "mt-2 h-12 w-full rounded-sm border border-border bg-background px-4 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-nook-green";
const labelClass = "text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground";

function Page() {
  const formRef = useRef<HTMLDivElement>(null);
  const prepareUpload = useServerFn(createResumeUpload);
  const submit = useServerFn(submitJobApplication);

  const [availability, setAvailability] = useState<string[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const toggleAvailability = (value: string) =>
    setAvailability((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth" });

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (!file) return setError("Anexe seu currículo em PDF, DOC ou DOCX.");
    if (file.size > MAX_SIZE) return setError("O currículo deve ter no máximo 5 MB.");
    if (availability.length === 0) return setError("Selecione ao menos uma disponibilidade.");
    if (!consent) return setError("É necessário autorizar o uso dos dados.");

    const data = new FormData(event.currentTarget);
    setLoading(true);
    try {
      const { path, token } = await prepareUpload({
        data: { fileName: file.name, size: file.size },
      });

      const upload = await supabase.storage.from("resumes").uploadToSignedUrl(path, token, file);
      if (upload.error) throw new Error("Falha ao enviar o arquivo do currículo.");

      await submit({
        data: {
          full_name: String(data.get("full_name") ?? ""),
          whatsapp: String(data.get("whatsapp") ?? ""),
          email: String(data.get("email") ?? ""),
          interest_area: String(data.get("interest_area") ?? ""),
          preferred_unit: String(data.get("preferred_unit") ?? ""),
          availability,
          resume_path: path,
          privacy_consent: true,
        },
      });

      setDone(true);
      window.scrollTo({ top: formRef.current?.offsetTop ?? 0, behavior: "smooth" });
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : "Não foi possível enviar agora. Tente novamente em instantes.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {/* Hero */}
      <section className="relative isolate overflow-hidden">
        <img
          src={heroImg.url}
          alt="Instrutor da Nook acompanhando um aluno durante o treino"
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/75" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-28 lg:px-8 lg:pb-28 lg:pt-36">
          <Breadcrumbs items={[{ label: "Início", to: "/" }, { label: "Trabalhe conosco" }]} />
          <p className="eyebrow mt-8 text-nook-green">Trabalhe conosco</p>
          <h1 className="display mt-4 max-w-3xl text-4xl leading-[1.05] sm:text-6xl lg:text-7xl">
            Faça parte
            <br className="sm:hidden" /> do time Nook.
          </h1>
          <p className="mt-6 max-w-xl text-base text-muted-foreground sm:text-lg">
            Buscamos pessoas presentes, responsáveis e comprometidas em entregar uma experiência de
            cuidado e excelência.
          </p>
          <button
            type="button"
            onClick={scrollToForm}
            className="mt-9 inline-flex h-12 items-center rounded-sm bg-nook-green px-7 text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-nook-green-foreground transition-all hover:brightness-110"
          >
            Enviar currículo
          </button>
        </div>
      </section>

      {/* Perfil */}
      <section className="border-t border-border py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="display text-3xl sm:text-4xl">Quem combina com a Nook.</h2>
            <p className="mt-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Na Nook, técnica, cuidado e presença fazem parte do mesmo trabalho. Procuramos
              profissionais que gostem de pessoas, saibam trabalhar em equipe e entendam que cada
              detalhe influencia a experiência do aluno.
            </p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {PROFILE.map((item) => (
              <div key={item.title} className="rounded-sm border border-border p-6">
                <item.icon className="h-5 w-5 text-nook-green" />
                <h3 className="mt-4 text-sm font-semibold uppercase tracking-[0.12em] text-foreground">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-snug text-muted-foreground whitespace-pre-line">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Formulário */}
      <section ref={formRef} className="scroll-mt-20 border-t border-border py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          {done ? (
            <div className="rounded-sm border border-nook-green/40 bg-nook-green/5 p-8 text-center">
              <Check className="mx-auto h-8 w-8 text-nook-green" />
              <h2 className="display mt-5 text-3xl sm:text-4xl">Currículo recebido.</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                Obrigado pelo interesse em fazer parte da Nook. Seu perfil foi enviado para o nosso
                banco de talentos e poderá ser considerado em oportunidades compatíveis.
              </p>
              <p className="mt-4 text-xs text-muted-foreground">
                O envio do currículo não garante contato imediato ou participação em processo
                seletivo.
              </p>
            </div>
          ) : (
            <>
              <h2 className="display text-3xl sm:text-4xl">Envie seu Currículo.</h2>
              <p className="mt-4 text-sm text-muted-foreground">
                Preencha as informações abaixo e envie seu currículo para o banco de talentos da
                Nook.
              </p>

              <form onSubmit={onSubmit} className="mt-10 space-y-6">
                <div>
                  <label className={labelClass} htmlFor="full_name">
                    Nome completo *
                  </label>
                  <input id="full_name" name="full_name" required maxLength={120} className={inputClass} />
                </div>

                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label className={labelClass} htmlFor="whatsapp">
                      WhatsApp *
                    </label>
                    <input
                      id="whatsapp"
                      name="whatsapp"
                      required
                      inputMode="tel"
                      placeholder="(21) 90000-0000"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass} htmlFor="email">
                      E-mail *
                    </label>
                    <input id="email" name="email" type="email" required className={inputClass} />
                  </div>
                </div>

                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label className={labelClass} htmlFor="interest_area">
                      Área de interesse *
                    </label>
                    <select id="interest_area" name="interest_area" required defaultValue="" className={inputClass}>
                      <option value="" disabled>
                        Selecione
                      </option>
                      {AREAS.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass} htmlFor="preferred_unit">
                      Local de interesse *
                    </label>
                    <select id="preferred_unit" name="preferred_unit" required defaultValue="" className={inputClass}>
                      <option value="" disabled>
                        Selecione
                      </option>
                      {UNIT_OPTIONS.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <p className={labelClass}>Disponibilidade de horário *</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {AVAILABILITY.map((slot) => {
                      const active = availability.includes(slot);
                      return (
                        <button
                          type="button"
                          key={slot}
                          onClick={() => toggleAvailability(slot)}
                          aria-pressed={active}
                          className={`h-10 rounded-full border px-4 text-xs uppercase tracking-[0.12em] transition-colors ${
                            active
                              ? "border-nook-green bg-nook-green text-nook-green-foreground"
                              : "border-border text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          {slot}
                        </button>
                      );
                    })}
                  </div>
                </div>


                <div>
                  <p className={labelClass}>Currículo * (PDF, DOC ou DOCX — até 5 MB)</p>
                  <label
                    htmlFor="resume"
                    className="mt-2 flex cursor-pointer items-center gap-3 rounded-sm border border-dashed border-border p-4 text-sm text-muted-foreground transition-colors hover:border-nook-green"
                  >
                    <Upload className="h-4 w-4 text-nook-green" />
                    <span className="truncate">{file ? file.name : "Selecionar arquivo"}</span>
                  </label>
                  <input
                    id="resume"
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="sr-only"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  />
                </div>

                <label className="flex items-start gap-3 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 accent-nook-green"
                  />
                  <span>
                    Autorizo o uso dos meus dados pela Nook Gym para processos seletivos atuais e
                    futuros, conforme a{" "}
                    <Link to="/politica-de-privacidade" className="text-nook-green underline">
                      Política de Privacidade
                    </Link>
                    .
                  </span>
                </label>

                {error && <p className="text-sm text-destructive">{error}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex h-12 items-center gap-2 rounded-sm bg-nook-green px-7 text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-nook-green-foreground transition-all hover:brightness-110 disabled:opacity-60"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {loading ? "Enviando..." : "Enviar currículo"}
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </>
  );
}
